
import { useState, useEffect, useRef } from 'react';
import { fetchBlogPost } from './useBlogFetch';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string }[];
  itineraries?: {
    id: string;
    title: string;
    destination?: string | null;
    slug?: string | null;
  }[];
};

export function usePublicBlogPost(slug: string) {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentSlugRef = useRef<string | null>(null);

  useEffect(() => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    const loadPost = async () => {
      if (!slug) {
        setError('No slug provided');
        setLoading(false);
        return;
      }

      // Prevent multiple requests for the same slug
      if (currentSlugRef.current === slug && post) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      currentSlugRef.current = slug;

      try {
        // Check if request was cancelled
        if (currentController.signal.aborted) return;

        const postData = await fetchBlogPost(slug);
        
        // Check again if request was cancelled after fetch
        if (currentController.signal.aborted) return;
        
        // Transform the tags structure to match our type
        const transformedTags = postData.tags?.map(tag => ({
          id: tag.tag_id.id,
          name: tag.tag_id.name
        })) || [];

        // Increment view count (don't await to avoid blocking UI)
        supabase
          .from('blog_posts')
          .update({ view_count: (postData.view_count || 0) + 1 })
          .eq('id', postData.id)
          .then(() => {
            // Silent update, no error handling needed
          });

        if (!postData) {
          setError('Blog post not found');
          setLoading(false);
          return;
        }

        // Check one more time if request was cancelled
        if (currentController.signal.aborted) return;

        setPost({
          ...postData,
          author: postData.author || undefined,
          tags: transformedTags,
          itineraries: postData.itineraries?.map(item => ({
            id: item.itineraries?.id || '',
            title: item.itineraries?.title || '',
            destination: item.itineraries?.destination,
            slug: item.itineraries?.slug
          }))
        });
        
        setLoading(false);
      } catch (err) {
        // Only handle error if request wasn't cancelled
        if (!currentController.signal.aborted) {
          console.error('Error fetching blog post:', err);
          setError('Failed to load blog post');
          setLoading(false);
        }
      }
    };

    loadPost();

    // Cleanup function
    return () => {
      if (currentController) {
        currentController.abort();
      }
    };
  }, [slug]);

  return { post, loading, error };
}
